import axios from 'axios'

class AdvancedAnalyticsService {
  constructor() {
    this.cache = new Map()
    this.cacheTimeout = 5 * 60 * 1000 // 5 minutes
  }

  // Master analytics function that returns EVERYTHING
  async getCompleteGameAnalysis(gameId, homeTeam, awayTeam) {
    const analysis = {
      winProbability: await this.calculateAdvancedWinProbability(homeTeam, awayTeam),
      pace: this.analyzePace(homeTeam, awayTeam),
      playStyle: this.analyzePlayStyles(homeTeam, awayTeam),
      keyMetrics: this.calculateKeyMetrics(homeTeam, awayTeam),
      injuries: await this.getInjuryImpact(homeTeam, awayTeam),
      h2h: await this.getHeadToHeadAnalysis(homeTeam, awayTeam),
      trends: this.analyzeTrends(homeTeam, awayTeam),
      matchupAdvantages: this.findMatchupAdvantages(homeTeam, awayTeam),
      bettingInsights: this.getStatisticalInsights(homeTeam, awayTeam),
      playerImpact: await this.analyzePlayerImpact(homeTeam, awayTeam)
    }

    return analysis
  }

  // ADVANCED WIN PROBABILITY MODEL
  calculateAdvancedWinProbability(homeTeam, awayTeam) {
    const factors = {
      // Basic team strength (30% weight)
      teamStrength: this.calculateTeamStrength(homeTeam, awayTeam),

      // Recent form (20% weight)
      recentForm: this.calculateFormFactor(homeTeam, awayTeam),

      // Head-to-head history (10% weight)
      h2hHistory: this.calculateH2HFactor(homeTeam, awayTeam),

      // Home court advantage (15% weight)
      homeAdvantage: 0.545, // NBA home teams win 54.5% historically

      // Injury impact (15% weight)
      injuryFactor: this.calculateInjuryFactor(homeTeam, awayTeam),

      // Rest advantage (10% weight)
      restFactor: this.calculateRestFactor(homeTeam, awayTeam)
    }

    // Weighted calculation
    const homeWinProb =
      factors.teamStrength * 0.30 +
      factors.recentForm * 0.20 +
      factors.h2hHistory * 0.10 +
      factors.homeAdvantage * 0.15 +
      factors.injuryFactor * 0.15 +
      factors.restFactor * 0.10

    return {
      home: homeWinProb,
      away: 1 - homeWinProb,
      confidence: this.calculateConfidence(factors),
      factors,
      reasoning: this.explainPrediction(factors, homeWinProb)
    }
  }

  // TEAM STRENGTH CALCULATION
  calculateTeamStrength(homeTeam, awayTeam) {
    const homeStats = homeTeam.stats || {}
    const awayStats = awayTeam.stats || {}

    // Offensive Rating (Points per 100 possessions)
    const homeOffRating = homeStats.offensiveRating || 110
    const awayOffRating = awayStats.offensiveRating || 110

    // Defensive Rating (Points allowed per 100 possessions)
    const homeDefRating = homeStats.defensiveRating || 110
    const awayDefRating = awayStats.defensiveRating || 110

    // Net Rating
    const homeNetRating = homeOffRating - homeDefRating
    const awayNetRating = awayOffRating - awayDefRating

    // Convert to probability (using logistic function)
    const ratingDiff = homeNetRating - awayNetRating
    const probability = 1 / (1 + Math.exp(-ratingDiff / 10))

    return probability
  }

  // PACE ANALYSIS (Possessions per game)
  analyzePace(homeTeam, awayTeam) {
    const homePace = homeTeam.stats?.pace || 100
    const awayPace = awayTeam.stats?.pace || 100

    const expectedPace = (homePace + awayPace) / 2
    const leagueAverage = 99.8 // NBA average

    return {
      expectedPace,
      homeTeamPace: homePace,
      awayTeamPace: awayPace,
      tempoRating: expectedPace > leagueAverage ? 'FAST' : 'SLOW',
      totalsPrediction: this.predictTotal(expectedPace, homeTeam, awayTeam),
      insights: [
        expectedPace > 105 ? '⚡ Expect a high-scoring, fast-paced game' : '',
        expectedPace < 95 ? '🐌 Expect a defensive, slow-paced battle' : '',
        Math.abs(homePace - awayPace) > 5 ? '⚔️ Pace clash - one team will dictate tempo' : ''
      ].filter(i => i)
    }
  }

  // PLAY STYLE ANALYSIS
  analyzePlayStyles(homeTeam, awayTeam) {
    const homeStyle = this.getTeamPlayStyle(homeTeam)
    const awayStyle = this.getTeamPlayStyle(awayTeam)

    return {
      home: homeStyle,
      away: awayStyle,
      matchupNotes: this.getStyleMatchupNotes(homeStyle, awayStyle),
      advantages: this.identifyStyleAdvantages(homeStyle, awayStyle)
    }
  }

  getTeamPlayStyle(team) {
    const stats = team.stats || {}

    // Shot distribution
    const threePointRate = stats.threePointAttemptRate || 0.35
    const paintPoints = stats.paintPointsPerGame || 45
    const fastBreakPoints = stats.fastBreakPoints || 12

    // Determine primary style
    let primaryStyle = 'Balanced'
    let characteristics = []

    if (threePointRate > 0.40) {
      primaryStyle = '3-Point Heavy'
      characteristics.push('🎯 Shoots 40%+ of shots from 3PT')
    } else if (paintPoints > 50) {
      primaryStyle = 'Paint Dominant'
      characteristics.push('💪 Scores 50+ points in paint')
    } else if (fastBreakPoints > 15) {
      primaryStyle = 'Transition Heavy'
      characteristics.push('🏃 15+ fast break points per game')
    }

    return {
      primary: primaryStyle,
      threePointRate: (threePointRate * 100).toFixed(1) + '%',
      twoPointRate: ((1 - threePointRate) * 100).toFixed(1) + '%',
      paintPoints,
      midRangeRate: stats.midRangeRate || 0.15,
      fastBreakPoints,
      characteristics,
      strengths: this.identifyStrengths(stats),
      weaknesses: this.identifyWeaknesses(stats)
    }
  }

  // KEY METRICS CALCULATION
  calculateKeyMetrics(homeTeam, awayTeam) {
    return {
      // Four Factors of Basketball Success
      fourFactors: {
        home: {
          eFG: this.calculateEffectiveFG(homeTeam), // Effective FG%
          turnoverRate: homeTeam.stats?.turnoverRate || 13.5,
          reboundRate: homeTeam.stats?.reboundRate || 50,
          freeThrowRate: homeTeam.stats?.freeThrowRate || 0.25
        },
        away: {
          eFG: this.calculateEffectiveFG(awayTeam),
          turnoverRate: awayTeam.stats?.turnoverRate || 13.5,
          reboundRate: awayTeam.stats?.reboundRate || 50,
          freeThrowRate: awayTeam.stats?.freeThrowRate || 0.25
        }
      },

      // Advanced metrics
      advanced: {
        home: {
          offensiveRating: homeTeam.stats?.offensiveRating || 110,
          defensiveRating: homeTeam.stats?.defensiveRating || 110,
          netRating: (homeTeam.stats?.offensiveRating || 110) - (homeTeam.stats?.defensiveRating || 110),
          trueShootingPct: this.calculateTrueShooting(homeTeam),
          assistRatio: homeTeam.stats?.assistRatio || 18
        },
        away: {
          offensiveRating: awayTeam.stats?.offensiveRating || 110,
          defensiveRating: awayTeam.stats?.defensiveRating || 110,
          netRating: (awayTeam.stats?.offensiveRating || 110) - (awayTeam.stats?.defensiveRating || 110),
          trueShootingPct: this.calculateTrueShooting(awayTeam),
          assistRatio: awayTeam.stats?.assistRatio || 18
        }
      }
    }
  }

  // INJURY IMPACT ANALYSIS
  async getInjuryImpact(homeTeam, awayTeam) {
    // In production, this would fetch from an injury API
    const mockInjuries = {
      home: [
        {
          player: 'Star Player',
          status: 'Questionable',
          injury: 'Ankle Sprain',
          gamesWissed: 3,
          impact: 'HIGH',
          winProbChange: -0.08,
          statsWithout: {
            record: '5-8',
            offRating: 105,
            defRating: 112
          }
        }
      ],
      away: []
    }

    return {
      home: mockInjuries.home,
      away: mockInjuries.away,
      totalImpact: this.calculateTotalInjuryImpact(mockInjuries),
      adjustedWinProb: this.adjustForInjuries(mockInjuries),
      keyInsights: this.getInjuryInsights(mockInjuries)
    }
  }

  // HEAD-TO-HEAD ANALYSIS
  async getHeadToHeadAnalysis(homeTeam, awayTeam) {
    // Mock H2H data - in production, fetch from API
    return {
      last10Meetings: {
        homeWins: 6,
        awayWins: 4,
        avgPointDiff: 3.5,
        avgTotal: 218
      },
      thisseason: {
        meetings: 2,
        homeWins: 1,
        awayWins: 1,
        scores: ['115-108', '102-99']
      },
      trends: [
        'Home team has won 6 of last 10',
        'Under has hit in 7 of last 10',
        'Average margin of victory: 7.2 points'
      ],
      dominantPlayers: {
        home: 'Player X averages 28.5 PPG vs this opponent',
        away: 'Player Y shoots 48% from 3PT vs this team'
      }
    }
  }

  // TREND ANALYSIS
  analyzeTrends(homeTeam, awayTeam) {
    return {
      home: {
        ats: {
          overall: '24-18-2',
          home: '14-8-1',
          last10: '7-3',
          asFavorite: '18-12-1',
          asUnderdog: '6-6-1'
        },
        totals: {
          over: '22-20-2',
          under: '20-22-2',
          last10: 'O 6-4',
          avgLine: 225.5,
          avgActual: 224.8
        },
        situational: [
          'Coming off a loss: 8-2 ATS',
          'In division games: 5-7 ATS',
          'As home favorite: 12-6 SU'
        ]
      },
      away: {
        ats: {
          overall: '20-22-2',
          away: '8-12-1',
          last10: '4-6',
          asFavorite: '10-8-1',
          asUnderdog: '10-14-1'
        },
        totals: {
          over: '25-19',
          under: '19-25',
          last10: 'U 7-3',
          avgLine: 228,
          avgActual: 226
        },
        situational: [
          'On 2+ days rest: 10-4 ATS',
          'Back-to-back road games: 2-8 ATS',
          'vs winning teams: 8-15 SU'
        ]
      }
    }
  }

  // MATCHUP ADVANTAGES
  findMatchupAdvantages(homeTeam, awayTeam) {
    const advantages = []

    // Pace advantage
    const paceDiff = (homeTeam.stats?.pace || 100) - (awayTeam.stats?.pace || 100)
    if (Math.abs(paceDiff) > 3) {
      advantages.push({
        team: paceDiff > 0 ? 'home' : 'away',
        type: 'PACE',
        description: `${paceDiff > 0 ? homeTeam.name : awayTeam.name} plays ${Math.abs(paceDiff).toFixed(1)} possessions faster`,
        impact: 'MEDIUM'
      })
    }

    // 3PT Defense vs 3PT Offense
    const home3PTDef = homeTeam.stats?.opp3PTPercent || 0.35
    const away3PTOff = awayTeam.stats?.threePTPercent || 0.35
    if (away3PTOff > 0.38 && home3PTDef > 0.36) {
      advantages.push({
        team: 'away',
        type: '3PT SHOOTING',
        description: `${awayTeam.name} elite 3PT shooting vs weak perimeter D`,
        impact: 'HIGH'
      })
    }

    // Rebounding advantage
    const homeReb = homeTeam.stats?.reboundRate || 50
    const awayReb = awayTeam.stats?.reboundRate || 50
    if (Math.abs(homeReb - awayReb) > 3) {
      advantages.push({
        team: homeReb > awayReb ? 'home' : 'away',
        type: 'REBOUNDING',
        description: `${homeReb > awayReb ? homeTeam.name : awayTeam.name} +${Math.abs(homeReb - awayReb).toFixed(1)}% rebound rate advantage`,
        impact: 'MEDIUM'
      })
    }

    return advantages
  }

  // STATISTICAL INSIGHTS
  getStatisticalInsights(homeTeam, awayTeam) {
    return {
      publicMoney: {
        spread: { home: 45, away: 55 },
        moneyline: { home: 38, away: 62 },
        total: { over: 67, under: 33 }
      },
      sharpMoney: {
        spread: { home: 60, away: 40 },
        moneyline: { home: 55, away: 45 },
        total: { over: 45, under: 55 }
      },
      lineMovement: {
        spread: { open: -3.5, current: -2.5, direction: 'away' },
        total: { open: 220, current: 218.5, direction: 'under' }
      },
      systemPicks: [
        {
          system: 'Contrarian Play',
          pick: `${homeTeam.name} +2.5`,
          reasoning: 'Sharp money opposing public',
          confidence: 65
        },
        {
          system: 'Pace Differential',
          pick: 'Under 218.5',
          reasoning: 'Both teams prefer slow pace',
          confidence: 70
        }
      ],
      edges: this.findStatisticalEdges(homeTeam, awayTeam)
    }
  }

  // PLAYER IMPACT ANALYSIS
  async analyzePlayerImpact(homeTeam, awayTeam) {
    // Mock key players data
    return {
      home: {
        keyPlayers: [
          {
            name: 'Star Player',
            ppg: 28.5,
            apg: 7.2,
            rpg: 8.1,
            usage: 31.2,
            plusMinus: +8.5,
            onCourt: { offRating: 118, defRating: 108 },
            offCourt: { offRating: 105, defRating: 112 },
            impact: 'CRITICAL',
            vsOpponent: {
              last5Games: '26.4 PPG, 8.8 RPG, 6.2 APG',
              career: '24.1 PPG on 47% shooting'
            }
          },
          {
            name: 'Second Star',
            ppg: 22.1,
            apg: 4.5,
            rpg: 5.3,
            usage: 26.8,
            plusMinus: +5.2,
            impact: 'HIGH'
          }
        ],
        benchImpact: {
          rating: 'B+',
          plusMinus: -2.3,
          scoringPerGame: 38.5
        }
      },
      away: {
        keyPlayers: [
          {
            name: 'Elite Scorer',
            ppg: 31.2,
            apg: 5.1,
            rpg: 6.7,
            usage: 33.5,
            plusMinus: +9.2,
            impact: 'CRITICAL'
          }
        ],
        benchImpact: {
          rating: 'C+',
          plusMinus: -5.1,
          scoringPerGame: 31.2
        }
      },
      matchupAdvantages: [
        'Home team deeper bench (+7.3 PPG advantage)',
        'Away team has best individual scorer',
        'Home team better supporting cast'
      ]
    }
  }

  // HELPER FUNCTIONS
  calculateEffectiveFG(team) {
    const fg = team.stats?.fieldGoalPercent || 0.45
    const threes = team.stats?.threesMade || 12
    const fga = team.stats?.fieldGoalAttempts || 85
    return ((fg * fga + 0.5 * threes) / fga * 100).toFixed(1)
  }

  calculateTrueShooting(team) {
    const points = team.stats?.pointsPerGame || 110
    const fga = team.stats?.fieldGoalAttempts || 85
    const fta = team.stats?.freeThrowAttempts || 20
    return (points / (2 * (fga + 0.44 * fta)) * 100).toFixed(1)
  }

  calculateFormFactor(homeTeam, awayTeam) {
    // Recent form based on last 10 games
    const homeLast10 = this.parseRecord(homeTeam.stats?.last10 || '5-5')
    const awayLast10 = this.parseRecord(awayTeam.stats?.last10 || '5-5')

    const homeForm = homeLast10.wins / 10
    const awayForm = awayLast10.wins / 10

    // Weight recent form
    return homeForm / (homeForm + awayForm)
  }

  parseRecord(record) {
    const [wins, losses] = record.split('-').map(n => parseInt(n) || 0)
    return { wins, losses }
  }

  calculateH2HFactor(homeTeam, awayTeam) {
    // Would use actual H2H data in production
    return 0.5 // Neutral for now
  }

  calculateInjuryFactor(homeTeam, awayTeam) {
    // Would use actual injury data in production
    const homeInjuryImpact = 0 // No injuries
    const awayInjuryImpact = 0.05 // Minor injury

    return 0.5 + homeInjuryImpact - awayInjuryImpact
  }

  calculateRestFactor(homeTeam, awayTeam) {
    const homeDaysRest = homeTeam.stats?.daysRest || 1
    const awayDaysRest = awayTeam.stats?.daysRest || 1

    // Back-to-back disadvantage
    if (homeDaysRest === 0 && awayDaysRest > 0) return 0.45
    if (awayDaysRest === 0 && homeDaysRest > 0) return 0.55

    // 2+ days rest advantage
    if (homeDaysRest >= 2 && awayDaysRest < 2) return 0.53
    if (awayDaysRest >= 2 && homeDaysRest < 2) return 0.47

    return 0.5 // Neutral
  }

  calculateConfidence(factors) {
    // Calculate confidence based on factor agreement
    const factorValues = Object.values(factors).filter(v => typeof v === 'number')
    const variance = this.calculateVariance(factorValues)

    // Lower variance = higher confidence
    const confidence = Math.max(0, Math.min(100, 85 - variance * 100))
    return Math.round(confidence)
  }

  calculateVariance(values) {
    const mean = values.reduce((a, b) => a + b, 0) / values.length
    const squaredDiffs = values.map(v => Math.pow(v - mean, 2))
    return Math.sqrt(squaredDiffs.reduce((a, b) => a + b, 0) / values.length)
  }

  explainPrediction(factors, probability) {
    const reasons = []

    if (factors.teamStrength > 0.55) {
      reasons.push('Superior team ratings and net differential')
    }
    if (factors.recentForm > 0.6) {
      reasons.push('Much better recent form (last 10 games)')
    }
    if (factors.injuryFactor > 0.55) {
      reasons.push('Opponent dealing with key injuries')
    }
    if (factors.restFactor > 0.52) {
      reasons.push('Rest advantage')
    }

    return reasons
  }

  predictTotal(pace, homeTeam, awayTeam) {
    const homeOffRating = homeTeam.stats?.offensiveRating || 110
    const awayOffRating = awayTeam.stats?.offensiveRating || 110
    const homeDefRating = homeTeam.stats?.defensiveRating || 110
    const awayDefRating = awayTeam.stats?.defensiveRating || 110

    // Expected points = (Pace * Offensive Rating * Opponent Defensive Rating) / (League Avg^2)
    const leagueAvg = 110
    const homePts = (pace * homeOffRating * awayDefRating) / (leagueAvg * leagueAvg)
    const awayPts = (pace * awayOffRating * homeDefRating) / (leagueAvg * leagueAvg)

    return {
      projected: Math.round(homePts + awayPts),
      home: Math.round(homePts),
      away: Math.round(awayPts),
      confidence: 'MEDIUM'
    }
  }

  getStyleMatchupNotes(homeStyle, awayStyle) {
    const notes = []

    if (homeStyle.primary === '3-Point Heavy' && awayStyle.primary === 'Paint Dominant') {
      notes.push('🎯 vs 💪: Classic perimeter vs paint battle')
    }

    if (homeStyle.primary === awayStyle.primary) {
      notes.push(`Mirror match: Both teams ${homeStyle.primary}`)
    }

    if (homeStyle.fastBreakPoints > 15 && awayStyle.fastBreakPoints < 10) {
      notes.push('🏃 Transition advantage for home team')
    }

    return notes
  }

  identifyStyleAdvantages(homeStyle, awayStyle) {
    const advantages = []

    if (parseFloat(homeStyle.threePointRate) > parseFloat(awayStyle.threePointRate) + 5) {
      advantages.push({
        team: 'home',
        description: 'Significant 3PT volume advantage'
      })
    }

    if (homeStyle.paintPoints > awayStyle.paintPoints + 8) {
      advantages.push({
        team: 'home',
        description: 'Dominates paint scoring'
      })
    }

    return advantages
  }

  identifyStrengths(stats) {
    const strengths = []

    if (stats.threePTPercent > 0.37) strengths.push('Elite 3PT shooting')
    if (stats.defensiveRating < 108) strengths.push('Top 10 defense')
    if (stats.reboundRate > 52) strengths.push('Elite rebounding')
    if (stats.assistRatio > 20) strengths.push('Excellent ball movement')

    return strengths
  }

  identifyWeaknesses(stats) {
    const weaknesses = []

    if (stats.turnoverRate > 15) weaknesses.push('Turnover prone')
    if (stats.freeThrowPercent < 0.72) weaknesses.push('Poor FT shooting')
    if (stats.opp3PTPercent > 0.37) weaknesses.push('Weak perimeter D')
    if (stats.benchPoints < 30) weaknesses.push('Weak bench')

    return weaknesses
  }

  calculateTotalInjuryImpact(injuries) {
    let totalImpact = 0

    injuries.home.forEach(inj => {
      if (inj.impact === 'HIGH') totalImpact -= 0.08
      if (inj.impact === 'MEDIUM') totalImpact -= 0.04
      if (inj.impact === 'LOW') totalImpact -= 0.02
    })

    injuries.away.forEach(inj => {
      if (inj.impact === 'HIGH') totalImpact += 0.08
      if (inj.impact === 'MEDIUM') totalImpact += 0.04
      if (inj.impact === 'LOW') totalImpact += 0.02
    })

    return totalImpact
  }

  adjustForInjuries(injuries) {
    const baseProb = 0.5
    const impact = this.calculateTotalInjuryImpact(injuries)
    return Math.max(0, Math.min(1, baseProb + impact))
  }

  getInjuryInsights(injuries) {
    const insights = []

    injuries.home.forEach(inj => {
      if (inj.impact === 'HIGH') {
        insights.push(`⚠️ ${inj.player} ${inj.status} - Team is ${inj.statsWithout.record} without him`)
      }
    })

    injuries.away.forEach(inj => {
      if (inj.impact === 'HIGH') {
        insights.push(`⚠️ ${inj.player} ${inj.status} - Major impact on away team`)
      }
    })

    return insights
  }

  findStatisticalEdges(homeTeam, awayTeam) {
    const edges = []

    // Identify potential edges based on data
    if (homeTeam.stats?.atsHome && homeTeam.stats.atsHome.includes('-')) {
      const [wins] = homeTeam.stats.atsHome.split('-').map(n => parseInt(n))
      if (wins > 10) {
        edges.push({
          type: 'ATS',
          description: `${homeTeam.name} strong ATS at home`,
          strength: 'MEDIUM'
        })
      }
    }

    return edges
  }
}

export default new AdvancedAnalyticsService()